import React, { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdmissionFormPage from './AdmissionFormPage';
import SchoolProfileSkeleton from '../SchoolProfileSkeleton';
import SchoolProfileNotFound from '../SchoolProfileNotFound';
import SchoolProfileError from '../SchoolProfileError';
import { useSchoolProfile } from '../../../hooks/useSchoolProfile';
import { isReservedPath, normalizePublicPath } from '../../../utils/reservedPaths';

/**
 * Route guard for /{publicPath}/admission.
 *
 * The school profile is loaded first so the form carries the school's name,
 * theme and academic calendar, and so an application can never be started for a
 * school that does not exist.
 */
const SchoolAdmissionRoute = () => {
  const { publicPath: rawPublicPath } = useParams();
  const publicPath = normalizePublicPath(rawPublicPath);
  const navigate = useNavigate();

  const { profile, isLoading, isNotFound, isError, retry } = useSchoolProfile(
    isReservedPath(publicPath) ? '' : publicPath
  );

  const backToProfile = useCallback(() => {
    navigate(`/${publicPath}`);
  }, [navigate, publicPath]);

  if (isReservedPath(publicPath)) return <SchoolProfileNotFound variant="page" />;
  if (isLoading) return <SchoolProfileSkeleton />;
  if (isNotFound) return <SchoolProfileNotFound variant="school" />;
  if (isError || !profile) return <SchoolProfileError onRetry={retry} />;

  return <AdmissionFormPage profile={profile} onBackToProfile={backToProfile} />;
};

export default SchoolAdmissionRoute;
